import { Button, Heading, IconButton } from '@assembly-js/design-system';
import copy from 'clipboard-copy';
import React, { useState } from 'react';

import { notify } from 'src/clients/ApiService';
import { User } from 'src/constants';
import { Contract } from 'src/entities/Contract';
import { CreateClientMagicLinkDialog } from 'src/features/magic-links/CreateClientMagicLinkDialog';
import { useCustomDomains } from 'src/hooks/useCustomDomains';
import { useAppDispatch } from 'src/hooks/useStore';
import { emitContractDownloadedAuditEvent } from 'src/legacy/components/Contracts/contract_helpers';
import { getFileNameFromFileKey } from 'src/legacy/components/Files/helpers';
import {
  ContractStatus,
  useCancelContractMutation,
} from 'src/services/api/contractsApi';
import { FileUtils, S3Utils, UrlUtils } from 'src/utils';

interface ContractPreviewModalHeaderProps {
  contract: Contract;
  onClose: () => void;
  enableMagicLinks: boolean;
  clientsMap: Map<string, User>;
}

export const ContractPreviewModalHeader: React.FC<
  ContractPreviewModalHeaderProps
> = ({ contract, onClose, enableMagicLinks, clientsMap }) => {
  const [cancelContract] = useCancelContractMutation();
  const { basePortalDomain } = useCustomDomains();
  const dispatch = useAppDispatch();
  const [userToInvite, setUserToInvite] = useState<User>();

  const handleCopyShareLink = async () => {
    const shareContractLink = UrlUtils.GetFullUrl(
      `${basePortalDomain}/sl/contract/${contract.ref}`,
    );
    try {
      await copy(shareContractLink);
      notify({
        status: 'success',
        successMessage: 'eSign link copied to clipboard',
        dispatch,
      });
    } catch {
      notify({
        status: 'error',
        errorMessage: 'Failed to copy eSign link',
        dispatch,
      });
    }
  };

  const handleCopyESignLink = () => {
    if (enableMagicLinks) {
      const user = clientsMap.get(contract.recipientId);
      if (user) {
        setUserToInvite(user);
        return;
      }
    }
    handleCopyShareLink();
  };

  const handleCancelContract = async () => {
    const result = await cancelContract({ id: contract.id });
    if ('error' in result) {
      notify({
        status: 'error',
        errorMessage: 'Failed to cancel contract',
        dispatch,
      });
      return;
    }
    onClose();
  };

  const handleDownload = async () => {
    const { signedFileUrl, fileUrl, status, identityId } = contract;
    const stagedFileKey =
      status === ContractStatus.Signed ? signedFileUrl : fileUrl;
    const s3Url = await S3Utils.getFile(stagedFileKey, {
      identityId,
      level: 'protected',
    });
    await FileUtils.downloadFileFromUrl(
      s3Url,
      stagedFileKey,
      getFileNameFromFileKey(stagedFileKey),
    );
    emitContractDownloadedAuditEvent(contract);
  };

  const renderActions = () => {
    if (contract.status === ContractStatus.Pending) {
      return (
        <>
          <Button
            label="Copy eSign link"
            variant="secondary"
            prefixIcon="Copy"
            onClick={handleCopyESignLink}
            size="sm"
          />
          <Button
            label="Cancel request"
            variant="secondary"
            prefixIcon="Trash"
            onClick={handleCancelContract}
            size="sm"
            className="border-red-primary text-red-primary hover:bg-red-primary/10"
          />
        </>
      );
    }

    if (contract.status === ContractStatus.Signed) {
      return (
        <Button
          label="Download"
          variant="secondary"
          prefixIcon="Download"
          onClick={handleDownload}
          size="sm"
        />
      );
    }

    return null;
  };

  return (
    <>
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-solid border-x-0 border-t-0 border-non-hover-border bg-white px-4 sm:px-6 py-4 h-15">
        <div className="flex shrink-0 items-center gap-2 sm:gap-3 overflow-hidden pr-4">
          <IconButton
            icon="Close"
            onClick={onClose}
            label="Close"
            variant="minimal"
            className="text-black-heading"
            size="sm"
          />
          <div className="border border-solid border-y-0 border-t-0 border-non-hover-border h-5 hidden sm:block" />
          <Heading className="text-sm sm:text-lg text-black-heading truncate">
            {contract.name}
          </Heading>
        </div>

        <div className="flex items-center gap-2.5">{renderActions()}</div>
      </div>
      {userToInvite && (
        <CreateClientMagicLinkDialog
          clients={[userToInvite]}
          entityType="CONTRACT"
          nextPath={`/contracts/submit?contractId=${contract.ref}`}
          onCancel={() => setUserToInvite(undefined)}
          onSuccess={() => setUserToInvite(undefined)}
          open={true}
          shareTypes={{ email: false, link: true }}
        />
      )}
    </>
  );
};
